/**
 * API client for Cosmic Cadet backend
 */

const API_BASE = 'https://cosmic-cadet-api.wyk747.workers.dev/api';

export interface SaveData {
  slot: number;
  level: number;
  score: number;
  lives: number;
  ammo: number;
  keys_collected: { yellow: boolean; red: boolean; blue: boolean };
  position_x: number;
  position_y: number;
  collected_items: string[];
  play_time: number;
  updated_at?: string;
}

export interface LeaderboardEntry {
  username: string;
  score: number;
  level: number;
  play_time: number;
  submitted_at: string;
}

/**
 * Game API client
 */
export class GameAPI {
  private playerId: string;

  constructor() {
    // Get or create player ID from localStorage
    let storedId = localStorage.getItem('cosmic_cadet_player_id');
    if (!storedId) {
      storedId = this.generateUUID();
      localStorage.setItem('cosmic_cadet_player_id', storedId);
    }
    this.playerId = storedId;
  }

  private generateUUID(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      const v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  private async fetch<T>(path: string, options: RequestInit = {}): Promise<T> {
    const response = await fetch(`${API_BASE}${path}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'X-Player-ID': this.playerId,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error((error as { error: string }).error || `HTTP ${response.status}`);
    }

    return response.json() as Promise<T>;
  }

  getPlayerId(): string {
    return this.playerId;
  }

  getUsername(): string | null {
    return localStorage.getItem('cosmic_cadet_username');
  }

  setUsername(username: string): void {
    localStorage.setItem('cosmic_cadet_username', username);
  }

  /**
   * Register player with the server
   */
  async register(username: string): Promise<void> {
    await this.fetch('/register', {
      method: 'POST',
      body: JSON.stringify({
        player_id: this.playerId,
        username,
      }),
    });
    this.setUsername(username);
  }

  /**
   * Get all saves for the player
   */
  async getSaves(): Promise<SaveData[]> {
    const result = await this.fetch<{ saves: SaveData[] }>('/saves');
    return result.saves;
  }

  /**
   * Save game state
   */
  async saveGame(data: Omit<SaveData, 'updated_at'>): Promise<void> {
    await this.fetch('/saves', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  /**
   * Delete a save slot
   */
  async deleteSave(slot: number): Promise<void> {
    await this.fetch(`/saves/${slot}`, {
      method: 'DELETE',
    });
  }

  /**
   * Get leaderboard entries
   */
  async getLeaderboard(limit = 10, offset = 0): Promise<{
    entries: LeaderboardEntry[];
    total: number;
  }> {
    return this.fetch(`/leaderboard?limit=${limit}&offset=${offset}`);
  }

  /**
   * Submit score to leaderboard
   */
  async submitScore(score: number, level: number, playTime: number): Promise<{ rank: number }> {
    return this.fetch('/leaderboard', {
      method: 'POST',
      body: JSON.stringify({
        score,
        level,
        play_time: playTime,
      }),
    });
  }
}

// Singleton instance
export const gameAPI = new GameAPI();
